import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import Sidebar from './Sidebar.jsx'
import Topbar from './Topbar.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { useCompany } from '../context/CompanyContext.jsx'
import api from '../lib/api.js'
import { isProduction, getEnvironment, ENV_COLOR } from '../lib/env.js'
import TermsAcceptanceDialog from '../features/auth/TermsAcceptanceDialog.jsx'

/* App shell: fixed brand sidebar + scrollable main column. Guards auth, forces
 * terms acceptance, and sends not-yet-configured companies to /app/setup. */
export default function AppLayout() {
  const { isAuthenticated, loading, user } = useAuth()
  const { currentCompanyId } = useCompany()
  const [needsSetup, setNeedsSetup] = useState(false)
  const needsTerms = !!user && (!user.accepted_terms_at || !user.accepted_privacy_at)

  useEffect(() => {
    if (!isAuthenticated || !currentCompanyId) return
    let active = true
    api.get('/onboarding/status').then(({ data }) => { if (active) setNeedsSetup(data?.setup_completed === false) }).catch(() => {})
    return () => { active = false }
  }, [isAuthenticated, currentCompanyId])

  if (loading) return <FullScreenLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  // Terms acceptance takes priority over the setup redirect (inventory §0).
  if (needsSetup && !needsTerms) return <Navigate to="/app/setup" replace />
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      {/* Non-prod environment indicator: a thin yellow bar on the sidebar/page
          seam. Absent in production. */}
      {!isProduction() && (
        <div title={getEnvironment()} style={{ width: 4, flex: 'none', background: ENV_COLOR[getEnvironment()] || '#f5b301' }} />
      )}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </main>
      {needsTerms && <TermsAcceptanceDialog />}
    </div>
  )
}

export function FullScreenLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '3px solid var(--color-border-strong)', borderTopColor: 'var(--color-primary)',
        animation: 'spin .7s linear infinite',
      }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  )
}

/*
 * Page = persistent Topbar (title + contextual action) + content framed at
 * max-width 1180px (DESIGN.md §4). Every app screen wraps its body in <Page>.
 */
export function Page({ title, action, children, maxWidth = 'var(--content-max)' }) {
  return (
    <>
      <Topbar title={title} action={action} />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '28px 28px 56px' }}>
          {children}
        </div>
      </div>
    </>
  )
}
